import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useUserData } from '@/libs/fleet-api/hooks';
import {
	getNAPSummaryConsent,
	NAPState,
	NAPSummaryConsentPayload,
	submitNAPSummaryConsent,
	verifyNAPSummaryConsent,
} from '@/libs/fleet-api/services/nap';
import { uploader } from '@/libs/fleet-api/uploader';
import { Alert, Button, Divider, Image, Tabs, Upload, UploadFile } from 'antd';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FC, Fragment } from 'react';
import { Form } from 'antd';
import rule from '@/libs/antd/rules';
import { FileData } from '@/libs/fleet-api/services/types';
import TextArea from 'antd/es/input/TextArea';
import { Viewer } from '@react-pdf-viewer/core';

const NAPSummaryConsent: FC<{
	napState?: NAPState;
	prospectId: string;
	customerId: string;
	onSubmitted: () => void;
}> = ({ napState, prospectId, customerId, onSubmitted }) => {
	// Preparations
	const { form, fieldName } = useForm<NAPSummaryConsentPayload>();
	const { groupName } = useUserData() ?? {};
	const isReadOnly =
		napState?.action === 'VIEW' || napState?.action === 'DISABLED';

	// Fetch Detail Data
	const getNAPSummaryConsentQuery = useQuery({
		queryKey: ['nap-summary-consent', prospectId],
		queryFn: () => getNAPSummaryConsent(prospectId),
	});
	const { data } = getNAPSummaryConsentQuery.data ?? {};

	// Upload Mutation
	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	// Submit Mutation
	const submitNAPSummaryConsentMutation = useMutation({
		mutationFn: submitNAPSummaryConsent,
	});

	const verifyNAPSummaryConsentMutation = useMutation({
		mutationFn: verifyNAPSummaryConsent,
	});

	const renderDocument = ({ format, name, url }: FileData) => {
		if (format === null) {
			if (name.endsWith('.pdf')) {
				return <Viewer fileUrl={url} />;
			}
			return <Image src={url} />;
		} else {
			switch (format) {
				case 'pdf':
					return <Viewer fileUrl={url} />;
				default:
					return <Image src={url} />;
			}
		}
	};

	// Document List
	const SummaryConsentDocs = () => {
		return (
			<Tabs
				items={data?.summaryConsentDocs?.map((doc, index) => ({
					key: String(index + 1),
					label: doc.docName,
					children: (
						<div
							style={{
								maxHeight: window.innerWidth < 768 ? '80vh' : '60vh',
								overflowY: 'auto',
							}}
						>
							{renderDocument(doc.document)}
						</div>
					),
				}))}
			/>
		);
	};

	if (getNAPSummaryConsentQuery.isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{
					span: 5,
				}}
				wrapperCol={{
					span: 19,
				}}
				onFinish={async (values) => {
					values.id = data?.id;
					values.napId = data?.napId;

					if (groupName === 'FRM') {
						await submitNAPSummaryConsentMutation.mutateAsync(values);
					} else if (groupName === 'CCA') {
						await verifyNAPSummaryConsentMutation.mutateAsync(values);
					}
					onSubmitted();
				}}
			>
				<Alert
					message="Diisi dengan Summary atau Highlight dari Consent Approval dan CES yang disampaikan dari Bank Mandiri"
					type="info"
					showIcon
					style={{ marginBottom: 20 }}
				/>

				{/* Document List */}
				{SummaryConsentDocs()}
				<Divider />

				{/* Company Profile Summary */}
				<Form.Item
					name={fieldName('companyProfileSummaryFile')}
					initialValue={data?.companyProfileSummaryFile}
					label="Upload Dokumen"
					style={{ marginBottom: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(fieldName('companyProfileSummaryFile'), null);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/temp`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});

								form.setFieldValue(fieldName('companyProfileSummaryFile'), {
									format: 'jpeg',
									id: fileid,
									name: file.name,
									path: filepath,
									url,
								});
							} catch {
								form.setFieldValue(
									fieldName('companyProfileSummaryFile'),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Form.Item
					name={fieldName('companyProfileSummary')}
					initialValue={data?.companyProfileSummary}
					label="Summary Company Profile"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				{/* BMRI History */}
				<Form.Item
					name={fieldName('bmriHistoryFile')}
					initialValue={data?.bmriHistoryFile}
					label="Upload Dokumen"
					style={{ marginBottom: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(fieldName('bmriHistoryFile'), null);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/temp`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});

								form.setFieldValue(fieldName('bmriHistoryFile'), {
									format: 'jpeg',
									id: fileid,
									name: file.name,
									path: filepath,
									url,
								});
							} catch {
								form.setFieldValue(fieldName('bmriHistoryFile'), null);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Form.Item
					name={fieldName('bmriHistory')}
					initialValue={data?.bmriHistory}
					label="History di BMRI"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				{/* Financing Requirement Summary */}
				<Form.Item
					name={fieldName('financingRequirementSummaryFile')}
					initialValue={data?.financingRequirementSummaryFile}
					label="Upload Dokumen"
					style={{ marginBottom: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(
								fieldName('financingRequirementSummaryFile'),
								null,
							);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/temp`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});

								form.setFieldValue(
									fieldName('financingRequirementSummaryFile'),
									{
										format: 'jpeg',
										id: fileid,
										name: file.name,
										path: filepath,
										url,
									},
								);
							} catch {
								form.setFieldValue(
									fieldName('financingRequirementSummaryFile'),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Form.Item
					name={fieldName('financingRequirementSummary')}
					initialValue={data?.financingRequirementSummary}
					label="Summary Kebutuhan Debitur Sebagai Dasar Pemberian Fasilitas Pembiayaan"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				{/* Balance Sheet Analysis */}
				<Form.Item
					name={fieldName('balanceSheetAnalysisFile')}
					initialValue={data?.balanceSheetAnalysisFile}
					label="Upload Dokumen"
					style={{ marginBottom: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(fieldName('balanceSheetAnalysisFile'), null);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/temp`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});

								form.setFieldValue(fieldName('balanceSheetAnalysisFile'), {
									format: 'jpeg',
									id: fileid,
									name: file.name,
									path: filepath,
									url,
								});
							} catch {
								form.setFieldValue(fieldName('balanceSheetAnalysisFile'), null);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Form.Item
					name={fieldName('balanceSheetAnalysis')}
					initialValue={data?.balanceSheetAnalysis}
					label="Analisa dari Neraca"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				{/* Profit Loss Analysis */}
				<Form.Item
					name={fieldName('profitLossAnalysisFile')}
					initialValue={data?.profitLossAnalysisFile}
					label="Upload Dokumen"
					style={{ marginBottom: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(fieldName('profitLossAnalysisFile'), null);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/temp`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});

								form.setFieldValue(fieldName('profitLossAnalysisFile'), {
									format: 'jpeg',
									id: fileid,
									name: file.name,
									path: filepath,
									url,
								});
							} catch {
								form.setFieldValue(fieldName('profitLossAnalysisFile'), null);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Form.Item
					name={fieldName('profitLossAnalysis')}
					initialValue={data?.profitLossAnalysis}
					label="Analisa dari Laba Rugi"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				{/* Cash Flow Analysis */}
				<Form.Item
					name={fieldName('cashFlowAnalysisFile')}
					initialValue={data?.cashFlowAnalysisFile}
					label="Upload Dokumen"
					style={{ marginBottom: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(fieldName('cashFlowAnalysisFile'), null);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/temp`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});

								form.setFieldValue(fieldName('cashFlowAnalysisFile'), {
									format: 'jpeg',
									id: fileid,
									name: file.name,
									path: filepath,
									url,
								});
							} catch {
								form.setFieldValue(fieldName('cashFlowAnalysisFile'), null);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Form.Item
					name={fieldName('cashFlowAnalysis')}
					initialValue={data?.cashFlowAnalysis}
					label="Analisa dari Arus Kas"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				{/* Financial Ratio Analysis */}
				<Form.Item
					name={fieldName('financialRatioAnalysisFile')}
					initialValue={data?.financialRatioAnalysisFile}
					label="Upload Dokumen"
					style={{ marginBottom: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(fieldName('financialRatioAnalysisFile'), null);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/temp`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});

								form.setFieldValue(fieldName('financialRatioAnalysisFile'), {
									format: 'jpeg',
									id: fileid,
									name: file.name,
									path: filepath,
									url,
								});
							} catch {
								form.setFieldValue(
									fieldName('financialRatioAnalysisFile'),
									null,
								);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Form.Item
					name={fieldName('financialRatioAnalysis')}
					initialValue={data?.financialRatioAnalysis}
					label="Analisa dari Rasio Keuangan"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				{/* SLIK Analysis */}
				<Form.Item
					name={fieldName('slikAnalysisFile')}
					initialValue={data?.slikAnalysisFile}
					label="Upload Dokumen"
					style={{ marginBottom: 20 }}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						style={{ width: 260 }}
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						disabled={isReadOnly}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(fieldName('slikAnalysisFile'), null);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${customerId}/${prospectId}/temp`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});

								form.setFieldValue(fieldName('slikAnalysisFile'), {
									format: 'jpeg',
									id: fileid,
									name: file.name,
									path: filepath,
									url,
								});
							} catch {
								form.setFieldValue(fieldName('slikAnalysisFile'), null);
							}
						}}
					>
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					</Upload.Dragger>
				</Form.Item>
				<Form.Item
					name={fieldName('slikAnalysis')}
					initialValue={data?.slikAnalysis}
					label="Analisa SLIK"
					rules={[rule.required()]}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly}
					/>
				</Form.Item>
				<Divider />

				{/* Additional Notes */}
				<Form.Item
					label={groupName === 'FRM' ? 'Informasi Tambahan' : 'Notes FRM'}
					name={fieldName('notes')}
					initialValue={data?.notes}
					rules={groupName === 'FRM' ? [rule.required()] : []}
				>
					<TextArea
						rows={3}
						disabled={isReadOnly || groupName !== 'FRM'}
					/>
				</Form.Item>
				{groupName !== 'FRM' && (
					<>
						<Divider />
						<Form.Item
							label={groupName === 'CCA' ? 'Verification Notes' : 'Notes CCA'}
							style={{ marginTop: 20 }}
							name={fieldName('verificationNotes')}
							initialValue={data?.verificationNotes}
							rules={groupName === 'CCA' ? [rule.required()] : []}
						>
							<TextArea
								rows={3}
								disabled={isReadOnly || groupName !== 'CCA'}
							/>
						</Form.Item>
					</>
				)}
				<Divider />

				<Button
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 40,
					}}
					disabled={
						submitNAPSummaryConsentMutation.isPending ||
						verifyNAPSummaryConsentMutation.isPending ||
						isReadOnly
					}
				>
					NEXT
				</Button>
			</Form>
		</Fragment>
	);
};

export default NAPSummaryConsent;
